/* eslint-disable max-len */

import { useTranslation } from 'react-i18next';
import {
  capitalizeFirstLetterEveryword
} from '../../../shared/stringFunctions';
import { TXT_ADD_VEIN_QUALIFIER, TXT_VEIN_QUALIFIERS } from '../../../translations/translationConstants';
import {
  TXT_SEARCH_BY_NAME
} from '../../../translations/translationConstants';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import {
  addVeinQualifierInput,
  deleteVeinQualifierInput,
  getLithologiesInput,
  getVeinQualifierOutput,
  GET_VEIN_QUALIFIER,
  updateVeinQualifierInput
} from '../../../api';
import { useEffect } from 'react';
import { useState } from 'react';
import { ManageVeinQualifiersTable } from './ManageVeinQualifierTable';
import {
  DeleteConfirmation,
  ExportToExcel,
  globalPageSizeOptions,
  PaginationBar
} from '../../../components';
import { veinQualifier } from '../../../entityModel';
import { useApolloErrorHandler, useAuthServices } from '../../../hooks';
import {
  ADD_VEIN_QUALIFIER,
  DELETE_VEIN_QUALIFIER,
  UPDATE_VEIN_QUALIFIER
} from '../../../api';
import { ManageVeinQualifierEditor } from './ManagerVeinQualifierEditor';

export const ManageVeinQualifiers = () => {
  const { t } = useTranslation();
  const { isSignedIn } = useAuthServices();

  const [txtSearch, setTxtSearch] = useState('');
  const [alterationData, setAlterationData] = useState<veinQualifier[] | undefined>([]);

  const onTxtSearchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTxtSearch(event.currentTarget.value);
  };

  const [selectedVeinQualifier, setSelectedVeinQualifier] = useState<veinQualifier | undefined>(undefined);
  const [getVeinQualifiers, { loading, data, error, refetch }] = useLazyQuery<getVeinQualifierOutput>(GET_VEIN_QUALIFIER);
  const [getAllAlterations] = useLazyQuery<getVeinQualifierOutput>(GET_VEIN_QUALIFIER, {
    onCompleted: (data) => setAlterationData(data.veinQualifiers.items)
  });

  useEffect(() => {
    getVeinQualifiers();
    getAllAlterations();
  }, []);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(globalPageSizeOptions[0]);

  const [openEditor, setOpenEditor] = useState(false);
  const [savingError, setSavingError] = useState<string | undefined>(undefined);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const lblSearch = capitalizeFirstLetterEveryword(t(TXT_SEARCH_BY_NAME));

  const fetchVeinQualifiers = () => {
    const variables: getLithologiesInput = {
      code: txtSearch,
      take: pageSize,
      skip: (pageNumber - 1) * pageSize
    };
    getVeinQualifiers({
      variables
    });
  };

  const fetch = (resetPage?: boolean) => {
    if (!!resetPage && pageNumber !== 1) {
      setPageNumber(1);
    } else {
      fetchVeinQualifiers();
    }
  };

  useEffect(() => {
    useApolloErrorHandler(isSignedIn, error);
  }, [error]);

  useEffect(() => {
    fetch();
  }, [pageNumber]);

  useEffect(() => {
    fetch(true);
  }, [pageSize, txtSearch]);

  const openEditVeinQualifierEditor = (veinQualifier: veinQualifier) => {
    setSelectedVeinQualifier(veinQualifier);
    setOpenEditor(true);
  };

  const openDeleteVeinQualifierConfirmation = (veinQualifier: veinQualifier) => {
    setSelectedVeinQualifier(veinQualifier);
    setOpenDeleteConfirmation(true);
  };

  const openAddVeinQualifierEditor = () => {
    setSelectedVeinQualifier(undefined);
    setOpenEditor(true);
  };

  const closeEditor = () => {
    setOpenEditor(false);
  };

  const afterAddingOrUpdatingVeinQualifier = () => {
    setOpenEditor(false);
    if (refetch) {
      refetch();
    }
  };

  const handleAddingOrUpdatingVeinQualifierError = (error: ApolloError) => {
    if (error.graphQLErrors.length > 0 &&
      !!error.graphQLErrors[0].extensions) {
      setSavingError(error.graphQLErrors[0].extensions['message']);
    } else {
      setSavingError(error.message);
    }
  };

  const [addVeinQualifier, addVeinQualifierOperation] = useMutation<veinQualifier>(ADD_VEIN_QUALIFIER, {
    onError: (e) => handleAddingOrUpdatingVeinQualifierError(e),
    onCompleted: (result) => afterAddingOrUpdatingVeinQualifier()
  });

  const [updateVeinQualifier, updateVeinQualifierOperation] = useMutation<veinQualifier>(UPDATE_VEIN_QUALIFIER, {
    onError: (e) => handleAddingOrUpdatingVeinQualifierError(e),
    onCompleted: (result) => afterAddingOrUpdatingVeinQualifier()
  });

  const onEditorSave = (payload: addVeinQualifierInput | updateVeinQualifierInput) => {
    if (!selectedVeinQualifier) {
      addVeinQualifier({
        variables: {
          input: payload
        }
      });
    } else {
      updateVeinQualifier({
        variables: {
          input: payload
        }
      });
    }
  };

  const onDeleteCancel = () => setOpenDeleteConfirmation(false);

  const [deleteVeinQualifier, deleteVeinQualifierOperation] = useMutation<boolean>(DELETE_VEIN_QUALIFIER, {
    onError: (e) => { },
    onCompleted: (result) => {
      if (data && refetch) {
        if (data.veinQualifiers.items.length == 1) {
          const targetPage = pageNumber - 1 <= 0 ? 1 : pageNumber - 1;
          if (pageNumber !== targetPage) {
            setPageNumber(targetPage);
          } else {
            refetch();
          }
        } else {
          refetch();
        }
      }
      onDeleteCancel();
    }
  });

  const onDelete = () => {
    const input: deleteVeinQualifierInput = {
      id: selectedVeinQualifier!.id!
    };
    deleteVeinQualifier({
      variables: {
        input
      }
    });
  };

  const onPageSizeChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPageSize(+event.target.value);
  };

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>{t(capitalizeFirstLetterEveryword(TXT_VEIN_QUALIFIERS))}</h1>
            </div>
          </div>
        </div>
      </section>
      <section className="content">
        <div className="container-fluid">
          {
            openEditor &&
            <ManageVeinQualifierEditor
              veinQualifier={selectedVeinQualifier}
              onClose={closeEditor}
              onSave={onEditorSave}
              isSaving={
                !selectedVeinQualifier ? addVeinQualifierOperation.loading :
                  updateVeinQualifierOperation.loading
              }
              savingError={savingError}
            />
          }
          {
            openDeleteConfirmation &&
            <DeleteConfirmation
              loading={deleteVeinQualifierOperation.loading}
              onYes={onDelete}
              onNo={onDeleteCancel}
              objectName={selectedVeinQualifier?.label || ''}
            />
          }
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className='row'>
                    <div className='col-6'>
                      <div className="form-group">
                        <label>{lblSearch}</label>
                        <input type='text' className='form-control' value={txtSearch} onChange={onTxtSearchChanged} />
                      </div>
                    </div>
                  </div>
                  <div className='row'>
                    <div className='col-md-6'>
                      <button
                        className='btn btn-primary'
                        data-target="#modal-veinQualifier-editor"
                        data-toggle="modal"
                        disabled={loading}
                        onClick={openAddVeinQualifierEditor}>
                        {t(TXT_ADD_VEIN_QUALIFIER).toUpperCase()}
                      </button>
                    </div>
                    <div className="col-md-6" style={{ textAlign: 'right' }}>
                      <ExportToExcel filename={TXT_VEIN_QUALIFIERS} data={alterationData} />
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <ManageVeinQualifiersTable
                    loading={loading}
                    data={data}
                    error={error}
                    onEdit={openEditVeinQualifierEditor}
                    onDelete={openDeleteVeinQualifierConfirmation}
                  />
                </div>
                <PaginationBar
                  currentPage={pageNumber}
                  totalCount={data?.veinQualifiers.totalCount}
                  pageSize={pageSize}
                  onPageChanged={(page) => setPageNumber(page)}
                  onChangeRowsPerPage={onPageSizeChanged}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
